import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import '../App.css';
import './Events.css';
import '../components/Popup.css';
import PageHero from '../components/PageHero';
import Popup from '../components/Popup';
import { apiBaseUrl } from '../config/api';
import { externalCta, registrationAction } from '../events/discovery';
import { sendRegistrationMail } from '../registrationMail';
import { useSearchParams } from 'react-router-dom';

function EventPhoto({ event, className, keepFrame = false }) {
  const [failed, setFailed] = useState(false);
  if (!event?.image_url) return null;
  if (failed) {
    if (!keepFrame) return null;
    return <div className={`${className} event-card-photo-fallback`} aria-hidden="true" />;
  }
  return (
    <img
      className={className}
      src={event.image_url}
      alt={event.image_alt || event.title || ''}
      onError={() => setFailed(true)}
    />
  );
}

function EventMarks({ event }) {
  if (!event?.difficulty_label && !event?.purpose_label) return null
  return (
    <p className="event-marks">
      {event.purpose_label && <span className="event-mark">{event.purpose_label}</span>}
      {event.difficulty_label && <span className="event-mark">{event.difficulty_label}</span>}
    </p>
  )
}

function EventExtra({ event }) {
  const items = Array.isArray(event?.bring_items) ? event.bring_items.filter(Boolean) : []
  const link = externalCta(event)
  if (!event?.purpose_copy && !event?.involvement && !event?.organiser_name && !event?.refreshments && items.length === 0 && !event?.participant_notes && !link) return null
  return (
    <div className="event-extra">
      {event.purpose_copy && <p>{event.purpose_copy}</p>}
      {event.involvement && <p className="event-note">{event.involvement}</p>}
      {event.organiser_name && <p>Veranstalter: {event.organiser_name}</p>}
      {event.refreshments && <p className="event-note">Verpflegung: {event.refreshments}</p>}
      {items.length > 0 && <p>Mitbringen: {items.join(', ')}</p>}
      {event.participant_notes && <p className="event-note">{event.participant_notes}</p>}
      {link && <a className="btn btn-primary" href={event.external_url} target="_blank" rel="noopener noreferrer">{link}</a>}
    </div>
  )
}

function Events() {
  const [view, setView] = useState('list');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [previewEvent, setPreviewEvent] = useState(null);
  const [successData, setSuccessData] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    participants: 1,
    privacy: false,
    company_website: ''
  });
  const [isSending, setIsSending] = useState(false);
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', variant: 'success' });

  const formatPrice = (raw) => {
    if (!raw) return '';
    const trimmed = raw.toString().trim();
    if (trimmed === '') return '';
    const num = parseFloat(trimmed.replace(',', '.'));
    if (trimmed.toLowerCase() === 'kostenlos') return 'Kostenlos';
    if (isNaN(num)) return trimmed.includes('CHF') ? trimmed : `${trimmed} CHF`;
    return `${num.toFixed(2)} CHF`;
  };

  const loadEvents = useCallback(async (signal) => {
    setIsLoading(true);
    setError('');
    if (!apiBaseUrl) {
      setEvents([]);
      setError('API-Adresse fehlt. Beim Build REACT_APP_API_BASE_URL setzen (z. B. https://moveland-example.onrender.com).');
      setIsLoading(false);
      return;
    }
    try {
      const res = await fetch(`${apiBaseUrl}/api/events/`, { signal });
      const ct = res.headers.get('content-type') || '';
      let data = null;
      if (ct.includes('application/json')) {
        data = await res.json();
      }
      if (!res.ok) {
        setEvents([]);
        setError('Events konnten nicht geladen werden. Bitte später erneut versuchen.');
        return;
      }
      if (data == null) {
        setEvents([]);
        setError('Die API hat keine gültigen Event-Daten geliefert.');
        return;
      }
      const list = Array.isArray(data) ? data : Array.isArray(data.results) ? data.results : [];
      setEvents(list);
    } catch (e) {
      if (e.name !== 'AbortError') {
        setEvents([]);
        const net = typeof e?.message === 'string' && e.message.includes('Failed to fetch');
        setError(
          net
            ? 'Netzwerkfehler. Prüfe REACT_APP_API_BASE_URL und ob die API CORS_ALLOWED_ORIGINS für diese Webseite erlaubt.'
            : 'Events konnten nicht geladen werden. Bitte später erneut versuchen.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadEvents(controller.signal);
    return () => controller.abort();
  }, [loadEvents]);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  }, [events]);

  const dateKey = (iso) => {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatDateParts = (iso) => {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString('de-DE', { day: '2-digit' }),
      monthYear: d.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' }),
      weekday: d.toLocaleDateString('de-DE', { weekday: 'short' }),
    };
  };

  const formatTime = (event) => {
    const start = new Date(event.start_at);
    const startStr = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    if (!event.end_at) {
      return `${startStr} Uhr`;
    }
    const end = new Date(event.end_at);
    const endStr = end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return `${startStr} - ${endStr} Uhr`;
  };

  const maxParticipantsAllowed = (event) => {
    if (!event || event.capacity == null || (isEventFull(event) && event.waitlist_enabled)) {
      return 20;
    }
    const left = Math.max(0, Number(event.spots_left) || 0);
    return Math.min(20, left);
  };

  const emptyForm = { first_name: '', last_name: '', email: '', participants: 1, privacy: false, company_website: '' };

  const openEvent = (event) => {
    setSelectedEvent(event);
    setFormData(emptyForm);
    setSuccessData(null);
  };

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('event');
    if (!id || events.length === 0) return;
    const match = events.find((event) => String(event.id) === id);
    if (!match) return;
    const node = document.getElementById(`event-${match.id}`);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setSelectedEvent(match);
    setFormData({ first_name: '', last_name: '', email: '', participants: 1, privacy: false, company_website: '' });
  }, [events, searchParams]);

  const closeEvent = () => setSelectedEvent(null);
  const closePreview = () => setPreviewEvent(null);
  const openFromPreview = (event) => {
    setPreviewEvent(null);
    openEvent(event);
  };
  const isEventFull = (event) => Boolean(event?.is_full) || (event?.capacity != null && (event.spots_left ?? 0) <= 0);
  const registrationClosed = (event) => registrationAction(event) === 'moveland' && (event?.status === 'cancelled' || event?.registration_enabled === false || event?.registration_open === false);
  const canWaitlist = (event) => registrationAction(event) === 'moveland' && isEventFull(event) && event?.waitlist_enabled && !registrationClosed(event);
  const availabilityLabel = (event) => {
    if (event?.status === 'cancelled') return 'Abgesagt';
    if (registrationAction(event) === 'external') return 'Externe Anmeldung';
    if (registrationAction(event) === 'none') return 'Keine Anmeldung nötig';
    if (event?.registration_enabled === false) return 'Für dieses Event ist keine Online-Anmeldung verfügbar.';
    if (event?.registration_open === false) return 'Anmeldung geschlossen';
    if (isEventFull(event)) return 'Ausgebucht';
    if (event?.capacity == null) return '';
    const left = event.spots_left ?? 0;
    if (left <= 2) return left === 1 ? 'Nur noch 1 Platz' : `Nur noch ${left} Plätze`;
    return `Noch ${left} Plätze frei`;
  };
  const priceSummary = (event, count) => {
    const unit = formatPrice(event?.price_text);
    if (!unit) return { unit: '', total: '' };
    if (unit === 'Kostenlos') return { unit: 'Kostenlos', total: '' };
    const number = parseFloat(String(event.price_text).replace('CHF', '').replace(',', '.'));
    if (!number || count < 2) return { unit: `${unit} pro Person`, total: '' };
    return { unit: `${unit} pro Person`, total: `Gesamt: ${(number * count).toFixed(2)} CHF` };
  };

  const submitRegistration = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    const cap = maxParticipantsAllowed(selectedEvent);
    const participants = Math.min(Math.max(1, Number(formData.participants) || 1), cap);
    if (cap < 1 || participants > cap) {
      return;
    }
    setIsSending(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/events/${selectedEvent.id}/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim(),
          participants,
          privacy_accepted: formData.privacy,
          form_token: selectedEvent.registration_form_token,
          company_website: formData.company_website
        })
      });
      let body = {};
      try { body = await res.json(); } catch { body = {}; }
      if (!res.ok) {
        throw new Error(typeof body.detail === 'string' ? body.detail : '');
      }
      const registeredEvent = selectedEvent;
      const registeredEmail = formData.email.trim();
      let mailSent = true;
      try {
        await sendRegistrationMail(body.mail);
      } catch {
        mailSent = false;
      }
      await loadEvents();
      closeEvent();
      setSuccessData({ event: registeredEvent, email: registeredEmail, status: body.status, holdMinutes: body.hold_minutes, participants, mailSent });
    } catch (err) {
      const safe = typeof err?.message === 'string' && err.message && !err.message.includes('Failed to fetch')
        ? err.message
        : 'Bitte versuche es erneut oder kontaktiere uns direkt.';
      setPopup({
        isOpen: true,
        title: 'Anmeldung fehlgeschlagen',
        message: safe,
        variant: 'error'
      });
    } finally {
      setIsSending(false);
    }
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const calendarDays = [];

    days.forEach((day) => {
      calendarDays.push(
        <div key={`header-${day}`} className="calendar-day-header">
          {day}
        </div>
      );
    });

    const current = new Date(startDate);
    for (let i = 0; i < 42; i++) {
      const isCurrentMonth = current.getMonth() === month;
      const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
      const dayEvents = sortedEvents.filter((ev) => dateKey(ev.start_at) === key);

      calendarDays.push(
        <div
          key={i}
          className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''}`}
          onClick={() => {
            if (dayEvents.length === 1) setPreviewEvent(dayEvents[0]);
          }}
        >
          <div className="calendar-day-number">{current.getDate()}</div>
          {dayEvents.map((ev) => (
            <div
              key={ev.id}
              className={`calendar-event${isEventFull(ev) ? ' calendar-event--full' : ''}`}
              title={`${ev.title}${isEventFull(ev) ? ' – Ausgebucht' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setPreviewEvent(ev);
              }}
            >
              {ev.title}
            </div>
          ))}
        </div>
      );

      current.setDate(current.getDate() + 1);
    }

    return calendarDays;
  };

  const changeMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  return (
    <>
      <Popup
        isOpen={popup.isOpen}
        title={popup.title}
        message={popup.message}
        variant={popup.variant}
        onClose={() => setPopup((p) => ({ ...p, isOpen: false }))}
      />

      <PageHero title="Events" />

      <section className="content-section">
        <div className="container">
          <div className="events-header">
            <h2>Kommende Veranstaltungen</h2>
            {!isLoading && !error && sortedEvents.length > 0 && (
              <div className="view-toggle">
                <button className={`view-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>
                  Liste
                </button>
                <button className={`view-btn ${view === 'calendar' ? 'active' : ''}`} onClick={() => setView('calendar')}>
                  Kalender
                </button>
              </div>
            )}
          </div>

          {isLoading && (
            <div className="events-list">
              <div className="event-card">
                <div className="event-date">Status</div>
                <h3>Lade Events...</h3>
                <p>Bitte einen Moment Geduld.</p>
              </div>
            </div>
          )}

          {!isLoading && error && (
            <div className="events-list">
              <div className="event-card">
                <div className="event-date">Status</div>
                <h3>Fehler</h3>
                <p>{error}</p>
              </div>
            </div>
          )}

          {!isLoading && !error && sortedEvents.length === 0 && (
            <div className="events-empty">
              <div className="events-empty-icon">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect x="6" y="10" width="36" height="32" rx="3" stroke="currentColor" strokeWidth="2.5" fill="none"/>
                  <path d="M6 18h36" stroke="currentColor" strokeWidth="2.5"/>
                  <path d="M16 6v8M32 6v8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  <path d="M16 28h6M16 34h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                </svg>
              </div>
              <h3 className="events-empty-title">Noch keine Events geplant</h3>
              <p className="events-empty-sub">Neue Termine werden in Kürze hier veröffentlicht.<br/>Schau bald wieder vorbei!</p>
            </div>
          )}

          {!isLoading && !error && sortedEvents.length > 0 && view === 'calendar' && (
            <div className="events-calendar-view">
              <div className="calendar-navigation">
                <button className="calendar-nav-btn" onClick={() => changeMonth(-1)}>
                  ‹
                </button>
                <h3>{currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</h3>
                <button className="calendar-nav-btn" onClick={() => changeMonth(1)}>
                  ›
                </button>
              </div>
              <div className="calendar-grid">{renderCalendar()}</div>
            </div>
          )}

          {!isLoading && !error && sortedEvents.length > 0 && view === 'list' && (
            <div className="events-list">
              {sortedEvents.map((event) => {
                const parts = formatDateParts(event.start_at);
                const full = isEventFull(event);
                const closed = registrationClosed(event);
                const waiting = canWaitlist(event);
                const places = availabilityLabel(event);
                return (
                  <div
                    key={event.id}
                    id={`event-${event.id}`}
                    className={`event-card${full ? ' event-card--full' : ''}${event.image_url ? ' event-card--photo' : ''}`}
                    onClick={() => openEvent(event)}
                  >
                    <EventPhoto event={event} className="event-card-photo" keepFrame />
                    <div className="event-card-datecol">
                      <span className="ecd-weekday">{parts.weekday}</span>
                      <span className="ecd-day">{parts.day}</span>
                      <span className="ecd-month">{parts.monthYear}</span>
                    </div>
                    <div className="event-card-body">
                      <div className="event-card-title-row">
                        <h3>{event.title}</h3>
                        <EventMarks event={event} />
                        {places && (
                          <span className={full || closed ? 'event-badge event-badge--full' : 'event-badge event-badge--spots'}>
                            {places}
                          </span>
                        )}
                      </div>
                      {event.description && (
                        <p className="event-card-desc">{event.description}</p>
                      )}
                      <div className="event-card-meta">
                        <span className="ecm-item">
                          <span className="ecm-label">Zeit</span>
                          {formatTime(event)}
                        </span>
                        {event.location && (
                          <span className="ecm-item">
                            <span className="ecm-label">Ort</span>
                            {event.location}
                          </span>
                        )}
                        {event.price_text && (
                          <span className="ecm-item">
                            <span className="ecm-label">Preis</span>
                            {formatPrice(event.price_text)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="event-card-action">
                      <button
                        className={closed || (full && !waiting) ? 'btn-ghost-disabled' : 'btn btn-primary event-card-btn'}
                        type="button"
                        disabled={closed || (full && !waiting)}
                        onClick={(e) => { e.stopPropagation(); openEvent(event); }}
                      >
                        {registrationAction(event) !== 'moveland' ? 'Event ansehen' : waiting ? 'Auf Warteliste setzen' : full ? 'Ausgebucht' : closed ? 'Geschlossen' : 'Platz sichern'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="cta-box">
            <p>Für weitere Informationen oder Anmeldungen:</p>
            <p>
              <strong>moveland.swiss@gmail.com</strong> | <strong>076/565 94 48</strong>
            </p>
          </div>
        </div>
      </section>

      {successData &&
        createPortal(
          <div className="popup-overlay" onClick={() => setSuccessData(null)}>
            <div className="reg-success-modal" onClick={(e) => e.stopPropagation()}>
              <div className="rsm-icon">✓</div>
              <h2 className="rsm-title">{successData.status === 'waitlist' ? 'Du bist auf der Warteliste' : 'Fast geschafft'}</h2>
              <p className="rsm-sub">
                {successData.mailSent === false
                  ? <>Deine Angaben sind gespeichert, aber die E-Mail an <strong>{successData.email}</strong> konnte nicht gesendet werden.</>
                  : successData.status === 'waitlist'
                  ? <>Wir haben dir eine E-Mail an <strong>{successData.email}</strong> geschickt. Wir melden uns, sobald ein passender Platz frei wird.</>
                  : <>Wir haben dir eine E-Mail an <strong>{successData.email}</strong> geschickt. Bestätige deine E-Mail, damit dein Platz definitiv für dich reserviert wird. Deine Plätze bleiben für {successData.holdMinutes || 60} Minuten reserviert.</>}
              </p>
              <div className="rsm-event-box">
                <div className="rsm-event-date">
                  {(() => { const p = formatDateParts(successData.event.start_at); return <><span className="rsm-day">{p.day}</span><span className="rsm-month">{p.monthYear}</span></>; })()}
                </div>
                <div className="rsm-event-info">
                  <strong>{successData.event.title}</strong>
                  <span>{formatTime(successData.event)}{successData.event.location ? ` · ${successData.event.location}` : ''}{successData.event.price_text ? ` · ${formatPrice(successData.event.price_text)}` : ''}</span>
                </div>
              </div>
              <button className="rsm-close" type="button" onClick={() => setSuccessData(null)}>
                Schliessen
              </button>
            </div>
          </div>,
          document.body
        )}

      {previewEvent &&
        createPortal(
          <div className="popup-overlay" onClick={closePreview}>
            <div className="cal-preview" onClick={(e) => e.stopPropagation()}>
              <button className="cal-preview-close" type="button" onClick={closePreview} aria-label="Schliessen">✕</button>
              <div className="cal-preview-datecol">
                {(() => { const p = formatDateParts(previewEvent.start_at); return <><span className="cpd-day">{p.day}</span><span className="cpd-month">{p.monthYear}</span></>; })()}
              </div>
              <div className="cal-preview-body">
                <h3 className="cal-preview-title">{previewEvent.title}</h3>
                <div className="cal-preview-meta">
                  <span>{formatTime(previewEvent)}</span>
                  {previewEvent.location && <><span className="cpm-sep">·</span><span>{previewEvent.location}</span></>}
                  {previewEvent.price_text && <><span className="cpm-sep">·</span><span className="cpm-price">{formatPrice(previewEvent.price_text)} / P.</span></>}
                </div>
                <EventMarks event={previewEvent} />
                {previewEvent.description && (
                  <p className="cal-preview-desc">{previewEvent.description}</p>
                )}
                <EventExtra event={previewEvent} />
                <div className="cal-preview-spots">
                  <span className={isEventFull(previewEvent) || registrationClosed(previewEvent) ? 'cps-full' : 'cps-open'}>{availabilityLabel(previewEvent) || 'Anmeldung offen'}</span>
                </div>
                <div className="cal-preview-actions">
                  {!registrationClosed(previewEvent) && (!isEventFull(previewEvent) || canWaitlist(previewEvent)) && (
                    <button className="btn btn-primary" type="button" onClick={() => openFromPreview(previewEvent)}>
                      {canWaitlist(previewEvent) ? 'Auf Warteliste setzen' : 'Platz sichern'}
                    </button>
                  )}
                  <button className="cal-preview-cancel" type="button" onClick={closePreview}>Schliessen</button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {selectedEvent &&
        createPortal(
          <div className="popup-overlay" onClick={closeEvent}>
            <div className="reg-modal" onClick={(e) => e.stopPropagation()}>

              <button className="reg-modal-close" type="button" onClick={closeEvent} aria-label="Schliessen">✕</button>
              <EventPhoto event={selectedEvent} className="reg-modal-photo" />

              <div className="reg-modal-event-info">
                <div className="reg-modal-event-date">
                  {(() => { const p = formatDateParts(selectedEvent.start_at); return <><span className="rmei-day">{p.day}</span><span className="rmei-month">{p.monthYear}</span></>; })()}
                </div>
                <div className="reg-modal-event-details">
                  <h2 className="reg-modal-title">{selectedEvent.title}</h2>
                  <div className="reg-modal-meta">
                    <span className="rmm-item">{formatTime(selectedEvent)}</span>
                    {selectedEvent.location && <span className="rmm-sep">·</span>}
                    {selectedEvent.location && <span className="rmm-item">{selectedEvent.location}</span>}
                    {selectedEvent.price_text && <span className="rmm-sep">·</span>}
                    {selectedEvent.price_text && <span className="rmm-item rmm-price">{formatPrice(selectedEvent.price_text)} / Person</span>}
                  </div>
                  <EventMarks event={selectedEvent} />
                  {selectedEvent.description && (
                    <p className="reg-modal-desc">{selectedEvent.description}</p>
                  )}
                  <EventExtra event={selectedEvent} />
                  <div className="reg-modal-spots">
                    <span className={isEventFull(selectedEvent) || registrationClosed(selectedEvent) ? 'rms-full' : 'rms-open'}>{availabilityLabel(selectedEvent) || 'Anmeldung offen'}</span>
                  </div>
                </div>
              </div>

              {registrationAction(selectedEvent) !== 'moveland' ? (
                <div className="reg-modal-full-notice">
                  <p>{availabilityLabel(selectedEvent)}</p>
                  <button className="btn btn-primary" type="button" onClick={closeEvent}>Schliessen</button>
                </div>
              ) : !registrationClosed(selectedEvent) && (!isEventFull(selectedEvent) || canWaitlist(selectedEvent)) ? (
                <form className="reg-modal-form" onSubmit={submitRegistration}>
                  <h3 className="reg-modal-form-title">{canWaitlist(selectedEvent) ? 'Warteliste' : 'Platz sichern'}</h3>
                  <p className="reg-summary-line">{selectedEvent.title}</p>
                  <p className="reg-summary-line">{formatTime(selectedEvent)}{selectedEvent.location ? ` · ${selectedEvent.location}` : ''}</p>
                  <p className="reg-summary-line">{formData.participants} {formData.participants === 1 ? 'Person' : 'Personen'}</p>
                  <p className="reg-summary-line">{priceSummary(selectedEvent, formData.participants).unit}</p>
                  {priceSummary(selectedEvent, formData.participants).total && <p className="reg-summary-line">{priceSummary(selectedEvent, formData.participants).total}</p>}
                  <div className="reg-form-grid">
                    <div className="form-group">
                      <label htmlFor="reg-first">Vorname</label>
                      <input id="reg-first" type="text" autoComplete="given-name" value={formData.first_name} onChange={(e) => setFormData((d) => ({ ...d, first_name: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="reg-last">Nachname</label>
                      <input id="reg-last" type="text" autoComplete="family-name" value={formData.last_name} onChange={(e) => setFormData((d) => ({ ...d, last_name: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="reg-email">E-Mail</label>
                      <input id="reg-email" type="email" autoComplete="email" value={formData.email} onChange={(e) => setFormData((d) => ({ ...d, email: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="reg-participants">Anzahl Personen</label>
                      <select id="reg-participants" value={formData.participants} onChange={(e) => setFormData((d) => ({ ...d, participants: Number(e.target.value) }))}>
                        {Array.from({ length: Math.max(1, maxParticipantsAllowed(selectedEvent)) }, (_, index) => index + 1).map((count) => <option key={count} value={count}>{count}</option>)}
                      </select>
                    </div>
                    <div className="hp-field" aria-hidden="true">
                      <label htmlFor="company_website">Website</label>
                      <input id="company_website" name="company_website" type="text" tabIndex={-1} autoComplete="off" value={formData.company_website} onChange={(e) => setFormData((d) => ({ ...d, company_website: e.target.value }))} />
                    </div>
                    <label className="reg-privacy">
                      <input type="checkbox" checked={formData.privacy} onChange={(e) => setFormData((d) => ({ ...d, privacy: e.target.checked }))} required />
                      Ich bin einverstanden, dass Moveland meine Angaben für diese Anmeldung speichert.
                    </label>
                  </div>
                  <div className="reg-modal-actions">
                    <button className="btn btn-primary reg-modal-submit" type="submit" disabled={isSending}>
                      {isSending ? 'Senden...' : canWaitlist(selectedEvent) ? 'Auf Warteliste setzen' : 'Platz sichern'}
                    </button>
                    <button className="reg-modal-cancel" type="button" onClick={closeEvent}>Abbrechen</button>
                  </div>
                </form>
              ) : (
                <div className="reg-modal-full-notice">
                  <p>{availabilityLabel(selectedEvent)}</p>
                  <button className="btn btn-primary" type="button" onClick={closeEvent}>Schliessen</button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default Events;

